import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { queryClient, persister } from "@/lib/queryClient";
import { AuthProvider } from "@/context/AuthContext";
import { SocketBridge } from "@/components/SocketBridge";
import { CartProvider } from "@/context/CartContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { AppShell } from "@/components/AppShell";
import { RequireAuth } from "@/components/ProtectedRoute";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Home from "@/pages/Home";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import CartCheckout from "@/pages/CartCheckout";
import Orders from "@/pages/Orders";
import Chats from "@/pages/Chats";
import Chat from "@/pages/Chat";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import Wallet from "@/pages/Wallet";
import Search from "@/pages/Search";
import SellerPage from "@/pages/SellerPage";
import PaymentResult from "@/pages/PaymentResult";
import DriverDashboard from "@/pages/driver/DriverDashboard";
import AddProduct from "@/pages/AddProduct";
import Favorites from "@/pages/Favorites";
import EditProfile from "@/pages/EditProfile";
import SettingsPage from "@/pages/Settings";
import InterestsOnboarding from "@/pages/InterestsOnboarding";
import Fund from "@/pages/Fund";
import MyPromotions from "@/pages/MyPromotions";
import Help from "@/pages/Help";
import Subscription from "@/pages/Subscription";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSection from "@/pages/admin/AdminSection";
import AdminLiveOrders from "@/pages/admin/AdminLiveOrders";
import AdminHelpEditor from "@/pages/admin/AdminHelpEditor";
import Assistant from "@/pages/Assistant";
import VisualSearch from "@/pages/VisualSearch";
import Watches from "@/pages/Watches";
import Drivers from "@/pages/Drivers";
import Consolidate from "@/pages/Consolidate";
import Track from "@/pages/Track";
import Browse from "@/pages/Browse";
import WalletSend from "@/pages/WalletSend";
import WalletWithdraw from "@/pages/WalletWithdraw";
import WalletHistory from "@/pages/WalletHistory";
import StoreCategories from "@/pages/StoreCategories";
import SellerPaymentMethods from "@/pages/SellerPaymentMethods";
import DriverVerify from "@/pages/DriverVerify";
import Placeholder from "@/pages/Placeholder";

function Shell({ children }) {
  return <AppShell>{children}</AppShell>;
}

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  return (
    <div className="App" dir="rtl">
      <GoogleOAuthProvider clientId={googleClientId}>
        <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
        <AuthProvider>
        <LanguageProvider>
          <CartProvider>
            <BrowserRouter>
              <Toaster position="top-center" richColors />
              <SocketBridge />
              <Routes>
                {/* Auth (no shell) */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/role-select" element={<RequireAuth><Placeholder title="اختيار نوع الحساب" /></RequireAuth>} />
                <Route path="/interests-onboarding" element={<RequireAuth><InterestsOnboarding /></RequireAuth>} />

                {/* Public / guest-friendly (shell) */}
                <Route path="/" element={<Shell><Home /></Shell>} />
                <Route path="/product/:id" element={<Shell><ProductDetail /></Shell>} />
                <Route path="/seller/:id" element={<Shell><SellerPage /></Shell>} />
                <Route path="/search" element={<Shell><Search /></Shell>} />
                <Route path="/browse/:id" element={<Shell><Browse /></Shell>} />
                <Route path="/payment/result" element={<Shell><PaymentResult /></Shell>} />
                <Route path="/payment/success" element={<Shell><PaymentResult /></Shell>} />
                <Route path="/payment/failed" element={<Shell><PaymentResult /></Shell>} />

                {/* Authenticated (shell) */}
                <Route path="/cart" element={<RequireAuth><Shell><Cart /></Shell></RequireAuth>} />
                <Route path="/cart/checkout" element={<RequireAuth><Shell><CartCheckout /></Shell></RequireAuth>} />
                <Route path="/checkout/:productId" element={<RequireAuth><Shell><Checkout /></Shell></RequireAuth>} />
                <Route path="/orders" element={<RequireAuth><Shell><Orders /></Shell></RequireAuth>} />
                <Route path="/chats" element={<RequireAuth><Shell><Chats /></Shell></RequireAuth>} />
                <Route path="/chat/order/:orderId" element={<RequireAuth><Shell><Chat order /></Shell></RequireAuth>} />
                <Route path="/chat/:userId" element={<RequireAuth><Shell><Chat /></Shell></RequireAuth>} />
                <Route path="/notifications" element={<RequireAuth><Shell><Notifications /></Shell></RequireAuth>} />
                <Route path="/profile" element={<RequireAuth><Shell><Profile /></Shell></RequireAuth>} />
                <Route path="/wallet" element={<RequireAuth><Shell><Wallet /></Shell></RequireAuth>} />
                <Route path="/wallet/send" element={<RequireAuth><Shell><WalletSend /></Shell></RequireAuth>} />
                <Route path="/wallet/withdraw" element={<RequireAuth><Shell><WalletWithdraw /></Shell></RequireAuth>} />
                <Route path="/wallet/history" element={<RequireAuth><Shell><WalletHistory /></Shell></RequireAuth>} />
                <Route path="/my-favorites" element={<RequireAuth><Shell><Favorites /></Shell></RequireAuth>} />
                <Route path="/watches" element={<RequireAuth><Shell><Watches /></Shell></RequireAuth>} />
                <Route path="/assistant" element={<RequireAuth><Shell><Assistant /></Shell></RequireAuth>} />
                <Route path="/visual-search" element={<RequireAuth><Shell><VisualSearch /></Shell></RequireAuth>} />
                <Route path="/consolidate" element={<RequireAuth><Shell><Consolidate /></Shell></RequireAuth>} />
                <Route path="/drivers" element={<RequireAuth><Shell><Drivers /></Shell></RequireAuth>} />
                <Route path="/track/:orderId" element={<RequireAuth><Shell><Track /></Shell></RequireAuth>} />
                <Route path="/settings" element={<RequireAuth><Shell><SettingsPage /></Shell></RequireAuth>} />
                <Route path="/edit-profile" element={<RequireAuth><Shell><EditProfile /></Shell></RequireAuth>} />

                {/* Seller */}
                <Route path="/add-product" element={<RequireAuth><Shell><AddProduct /></Shell></RequireAuth>} />
                <Route path="/edit-product/:id" element={<RequireAuth><Shell><AddProduct /></Shell></RequireAuth>} />
                <Route path="/store/categories" element={<RequireAuth><Shell><StoreCategories /></Shell></RequireAuth>} />
                <Route path="/seller/payment-methods" element={<RequireAuth><Shell><SellerPaymentMethods /></Shell></RequireAuth>} />
                <Route path="/my-promotions" element={<RequireAuth><Shell><MyPromotions /></Shell></RequireAuth>} />
                <Route path="/help" element={<RequireAuth><Shell><Help /></Shell></RequireAuth>} />
                <Route path="/subscription" element={<RequireAuth><Shell><Subscription /></Shell></RequireAuth>} />
                <Route path="/fund/:productId" element={<RequireAuth><Shell><Fund /></Shell></RequireAuth>} />
                <Route path="/promote/:productId" element={<RequireAuth><Shell><Fund /></Shell></RequireAuth>} />

                {/* Driver */}
                <Route path="/driver-dashboard" element={<RequireAuth><Shell><DriverDashboard /></Shell></RequireAuth>} />
                <Route path="/driver-verify" element={<RequireAuth><Shell><DriverVerify /></Shell></RequireAuth>} />
                <Route path="/driver/:id" element={<Shell><Placeholder title="السائق" /></Shell>} />

                {/* Admin */}
                <Route path="/admin" element={<RequireAuth adminOnly><Shell><AdminDashboard /></Shell></RequireAuth>} />
                <Route path="/admin-live-orders" element={<RequireAuth adminOnly><Shell><AdminLiveOrders /></Shell></RequireAuth>} />
                <Route path="/admin-help" element={<RequireAuth adminOnly><Shell><AdminHelpEditor /></Shell></RequireAuth>} />
                <Route path="/admin/:section" element={<RequireAuth adminOnly><Shell><AdminSection /></Shell></RequireAuth>} />

                <Route path="*" element={<Shell><Placeholder title="الصفحة غير موجودة" /></Shell>} />
              </Routes>
            </BrowserRouter>
          </CartProvider>
        </LanguageProvider>
      </AuthProvider>
        </PersistQueryClientProvider>
      </GoogleOAuthProvider>
    </div>
  );
}

export default App;
